import { FC, useEffect, useState, useCallback, memo } from "react";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { getSurveySection } from "app/features/surveySlice";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { YES } from "pages/SurveyForm/constants/uiControls";
import StepContent from "../StepContent";
import { FieldArray } from "formik";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import DeleteIcon from "@mui/icons-material/Delete";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, { AccordionSummaryProps } from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import { Button } from "@mui/material";
import FieldTextField from "pages/SurveyForm/components/ui/FieldTextField";
import FieldSelect from "pages/SurveyForm/components/ui/FieldSelect";
import FieldRadioGroup from "pages/SurveyForm/components/ui/FieldRadioGroup";
import { templatePerson, templatePersonsModel } from "pages/SurveyForm/constants/initialValues";
import ErrorMessage from "ui/atoms/ErrorMessage";

type Props = {
  formik: any;
  sectionId: number;
};

const Step13: FC<Props> = memo(({ formik, sectionId }) => {
  const dispatch = useAppDispatch();
  const { surveySection } = useAppSelector((state) => state.survey) ?? [];
  const { questions = [] } = surveySection || {};
  const [expanded, setExpanded] = useState<number | false>(false);

  const repeatTemplate = (arr: any, repeats: any) => [].concat(...Array.from({ length: repeats }, () => arr));

  useEffect(() => {
    dispatch(getSurveySection(sectionId));
  }, [sectionId]);

  useEffect(() => {
    const persons = formik?.values?.numberOfPersons?.value;
    if (persons > 0) {
      formik?.setFieldValue("template", repeatTemplate(templatePersonsModel, persons), false);
    }
  }, [formik?.values?.numberOfPersons?.value]);

  const handleChange = useCallback(
    (panel: number) => (event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    },
    [setExpanded]
  );
  return (
    <Container>
      <StepContent>
        <Typography mb={2} variant='subtitle1' align='center'>
          Agregue personas que convivan con usted diferente al cónyugue e hijos
        </Typography>
        <FieldArray
          name='template'
          validateOnChange={true}
          render={({ push, remove }: any) => {
            return (
              <>
                {formik?.errors?.template && typeof formik?.errors?.template === "string" ? (
                  <ErrorMessage margin='0'>{formik?.errors?.template}</ErrorMessage>
                ) : null}
                {formik?.values?.template?.map((template: any, index: number) => {
                  return (
                    !!questions[0]?.template?.length && (
                      <TemplateContainer>
                        <AccordionWrapper>
                          <Accordion expanded={expanded === index} onChange={handleChange(index)} key={template?.id}>
                            <AccordionSummary
                              expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
                              aria-controls='panel1a-content'
                              id='panel1a-header'
                            >
                              <Stack
                                spacing={1}
                                direction='row'
                                alignItems='center'
                                justifyContent='space-between'
                                sx={{ width: "100%" }}
                              >
                                <div>
                                  <Typography>Parentesco {index + 1}</Typography>
                                </div>
                              </Stack>
                            </AccordionSummary>

                            <AccordionDetails>
                              <div key={template?.id}>
                                <FieldTextField
                                  visible={true}
                                  label='Nombre'
                                  margin='normal'
                                  subSectionId={index + 1}
                                  name={`template.${index}.${templatePerson?.name}`}
                                />
                                <FieldSelect
                                  label='Género'
                                  margin='normal'
                                  subSectionId={index + 1}
                                  name={`template.${index}.${templatePerson?.gender}`}
                                  dataSource={questions[0]?.template[0]?.subQuestions[1]?.source ?? []}
                                />
                                <FieldSelect
                                  margin='normal'
                                  subSectionId={index + 1}
                                  label='Parentesco con el trabajador'
                                  name={`template.${index}.${templatePerson?.parentage}`}
                                  dataSource={questions[0]?.template[0]?.subQuestions[2]?.source ?? []}
                                />
                                <FieldSelect
                                  margin='normal'
                                  label='Rango edad'
                                  subSectionId={index + 1}
                                  name={`template.${index}.${templatePerson?.ageRange}`}
                                  dataSource={questions[0]?.template[0]?.subQuestions[3]?.source ?? []}
                                />
                                <FieldRadioGroup
                                  subSectionId={index + 1}
                                  label='¿Aporta económicamente al hogar?'
                                  name={`template.${index}.${templatePerson?.isContributor}`}
                                  dataSource={questions[0]?.template[0]?.subQuestions[4]}
                                />
                                <FieldRadioGroup
                                  subSectionId={index + 1}
                                  label='¿Depende económicamente del trabajador?'
                                  name={`template.${index}.${templatePerson?.isDependent}`}
                                  dataSource={questions[0]?.template[0]?.subQuestions[5]}
                                />
                                <FieldRadioGroup
                                  subSectionId={index + 1}
                                  label='¿Recibe subsidio familiar?'
                                  name={`template.${index}.${templatePerson?.isBenefit}`}
                                  dataSource={questions[0]?.template[0]?.subQuestions[6]}
                                />
                                <FieldRadioGroup
                                  subSectionId={index + 1}
                                  label='¿Presenta discapacidad?'
                                  name={`template.${index}.${templatePerson?.hasDisability}`}
                                  dataSource={questions[0]?.template[0]?.subQuestions[7]}
                                />
                                <FieldTextField
                                  label='¿Cual?'
                                  margin='normal'
                                  subSectionId={index + 1}
                                  name={`template.${index}.${templatePerson?.disabilityType}`}
                                  visible={
                                    formik?.values?.template?.[index]?.[`${templatePerson?.hasDisability}`] === YES
                                  }
                                />
                              </div>
                            </AccordionDetails>
                          </Accordion>
                        </AccordionWrapper>
                        {questions?.length > 0 ? (
                          <Delete>
                            <IconButton aria-label='delete' onClick={() => remove(index)}>
                              <DeleteIcon fontSize='small' />
                            </IconButton>
                          </Delete>
                        ) : null}
                      </TemplateContainer>
                    )
                  );
                })}
                {formik?.errors?.template?.length > 0 && (
                  <ErrorMessage margin='0'>Debe registrar la información completa de cada persona</ErrorMessage>
                )}
                <AddButtonWrapper>
                  <Button
                    variant='outlined'
                    onClick={() => push(templatePersonsModel)}
                    disabled={formik?.values?.template?.length === 25}
                  >
                    Agregar Persona
                  </Button>
                </AddButtonWrapper>
              </>
            );
          }}
        />
      </StepContent>
    </Container>
  );
});

const Container = styled("div")`
  padding-left: 2rem;
`;

const TemplateContainer = styled("div")`
  flex: 1;
  display: flex;
  align-items: center;
  flex-direction: row;
`;

const AddButtonWrapper = styled("div")`
  padding: 0.5rem 0;
`;

const AccordionWrapper = styled("div")`
  width: 100%;
`;

const Delete = styled("div")`
  right: 60px;
  margin-top: 5px;
  position: sticky;
  align-self: flex-start;
`;

const Accordion = styled((props: AccordionProps) => <MuiAccordion disableGutters elevation={0} square {...props} />)(
  ({ theme }) => ({
    border: `0.1px solid ${theme.palette.primary.main}`,
    margin: "1px",
    // "&:not(:last-child)": {
    //   borderBottom: 0,
    // },
    "&:before": {
      display: "none",
    },
  })
);

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />} {...props} />
))(({ theme }) => ({
  flexDirection: "row-reverse",
  "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
    transform: "rotate(90deg)",
  },
  "& .MuiAccordionSummary-content": {
    marginLeft: theme.spacing(1),
  },
}));

export default Step13;
